"use client";
import {
  InteractiveTableSteps,
  ChallengeDropdownSection,
  type Challenge,
  InfoTextSection,
  RegistrationFormSection,
} from "@/components/ITSMMaturityModel";
import { BuilderBlockProps, BuilderProps, CallbackReturnType } from "@/types";
import { FC, PropsWithChildren, useEffect, useRef } from "react";

type InteractiveTableStepsBlockProps = {
  step1Title?: string;
  step1Description?: string;
  step2Title?: string;
  step2Description?: string;
  step3ButtonText?: string;
  dld: string;
  dlt: string;
  step3ModalError?: {
    header?: string;
    description?: string;
  };
  step3UnderReview?: {
    header?: string;
    description?: string;
  };
  step3Title?: string;
  step1OptionalText?: string;
  step3Description?: string;
  placeholder?: string;
  challenges?: Challenge[];
  selectedChallenge?: Challenge | null;
  removeChallengeCta?: string;
  onChallengeChange?: (
    value: Challenge | null,
    elem: HTMLDivElement | null,
  ) => CallbackReturnType;
} & BuilderBlockProps &
  BuilderProps;

export const InteractiveTableStepsBlock: FC<
  PropsWithChildren<InteractiveTableStepsBlockProps>
> = ({
  attributes,
  children,
  step1Title,
  step1Description,
  step3Title,
  step1OptionalText,
  step3Description,
  step2Title,
  step2Description,
  step3ModalError,
  step3UnderReview,
  step3ButtonText,
  dlt,
  dld,
  placeholder = "Select an option",
  challenges = [],
  selectedChallenge,
  onChallengeChange,
  removeChallengeCta,
  builderContext,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const isUnderReview =
    (builderContext.rootState?.isUnderReview as boolean) ?? false;

  useEffect(() => {
    window.itsmSelectedChallenge = selectedChallenge?.value ?? undefined;
    return () => {
      delete window.itsmSelectedChallenge;
    };
  }, [selectedChallenge]);

  const handleChallengeChange = async (challenge: Challenge | null) => {
    if (onChallengeChange) {
      await onChallengeChange(challenge, ref.current);
    }
  };

  return (
    <InteractiveTableSteps
      ref={ref}
      {...attributes}
      selectedChallenge={selectedChallenge}
      setSelectedChallenge={handleChallengeChange}
    >
      <InfoTextSection header={step2Title} description={step2Description} />
      <RegistrationFormSection
        header={step3Title}
        description={step3Description}
        ctaName={step3ButtonText}
        incompleteTableError={step3ModalError}
        underReview={step3UnderReview}
        isUnderReview={isUnderReview}
        registrationForm={children}
        analytics={{ dlt, dld }}
      />
      <ChallengeDropdownSection
        header={step1Title}
        description={step1Description}
        optionalText={step1OptionalText}
        dropdown={{
          name: "challenges",
          challenges: challenges,
          placeholder: placeholder,
          removeChallengeCta: removeChallengeCta,
        }}
        selectedChallenge={selectedChallenge}
        setSelectedChallenge={handleChallengeChange}
      />
    </InteractiveTableSteps>
  );
};

export default InteractiveTableStepsBlock;
